/*!
 * pfm-track.js — PFM external API tracker for Shopify (theme snippet, no app required)
 *
 * Sends: ViewContent, AddToCart, InitiateCheckout  (+ SubmitForm / CompleteRegistration if you call them;
 *        CompleteRegistration is sent to PFM as type=AccountCreation)
 * Does NOT send: Sold — that fires from Shopify Flow on order creation (server side).
 *
 * Responsibilities
 *   1. Capture click identity (click id, platform click ids, UTMs, creator ref) on landing.
 *   2. Persist it first-party (cookie + localStorage) and mirror it onto the Shopify cart
 *      as cart attributes, so the order carries it for Shopify Flow -> PFM (Sold).
 *   3. Emit funnel events to the PFM external API with retry, dedupe and consent handling.
 *
 * Config comes from window.PFM_CONFIG (set in snippets/pfm-track.liquid).
 */
(function (window, document) {
  "use strict";

  var CFG = window.PFM_CONFIG || {};
  var DEFAULTS = {
    endpoint: "", // e.g. https://api.pfm.example.com/v1/events   (REQUIRED)
    writeKey: "", // public write key, sent as header/param — never a secret key
    siteId: "", // PFM site / property identifier
    campaignId: "", // optional giveaway or campaign id
    clickParams: ["clickId", "clickid", "click_id", "click-id", "pfm_cid", "cid"],
    entrantParams: ["pfm_eid", "entrant_id"],
    refParams: ["ref", "creator"],
    affParams: ["aff", "aff_name", "affiliate", "partner"],
    mbhParams: ["mbh"],
    clickIdEndpoint: "", // GET -> { clickId }; mints one for visitors with no aff/clickId. '' = off
    extraParams: ["ad_id", "adset_id", "campaign_id", "placement"],
    cookieDays: 30,
    cookieDomain: "", // '' = current host; '.brand.com' to share with the portal subdomain
    cartAttributes: true, // mirror ids onto the cart (needed for Sold via Flow)
    trackViewContent: true,
    trackAddToCart: true,
    trackInitiateCheckout: true,
    viewContentPages: ["product"], // 'product', 'collection', 'index', 'page', 'all'
    submitFormSelector: "", // optional CSS selector; submits fire SubmitForm
    requireConsent: false, // true = wait for Shopify customerPrivacy analytics consent
    dedupeWindowMs: 30000, // identical event+key inside this window is dropped
    maxQueue: 50,
    debug: false,
  };
  var C = {};
  for (var k in DEFAULTS) {
    C[k] =
      CFG[k] === undefined || CFG[k] === null || CFG[k] === ""
        ? DEFAULTS[k]
        : CFG[k];
  }

  var LS_ID = "pfm_identity";
  var LS_QUEUE = "pfm_queue";
  var LS_DEDUPE = "pfm_dedupe";
  var SESSION_KEY = "pfm_session";

  /* ---------------------------------------------------------------- utils */

  function log() {
    if (!C.debug || !window.console) return;
    var a = ["[pfm]"].concat([].slice.call(arguments));
    console.log.apply(console, a);
  }

  function uuid() {
    if (window.crypto && window.crypto.randomUUID) {
      try {
        return window.crypto.randomUUID();
      } catch (e) {}
    }
    var b = new Array(36),
      hex = "0123456789abcdef";
    for (var i = 0; i < 36; i++) b[i] = hex[(Math.random() * 16) | 0];
    b[8] = b[13] = b[18] = b[23] = "-";
    b[14] = "4";
    b[19] = hex[(parseInt(b[19], 16) & 0x3) | 0x8];
    return b.join("");
  }

  function now() {
    return new Date().toISOString();
  }

  function safeParse(s, fallback) {
    try {
      return JSON.parse(s);
    } catch (e) {
      return fallback;
    }
  }

  function lsGet(key, fallback) {
    try {
      var v = window.localStorage.getItem(key);
      return v ? safeParse(v, fallback) : fallback;
    } catch (e) {
      return fallback;
    }
  }

  function lsSet(key, val) {
    try {
      window.localStorage.setItem(key, JSON.stringify(val));
    } catch (e) {
      /* private mode */
    }
  }

  function ssGet(key) {
    try {
      return window.sessionStorage.getItem(key);
    } catch (e) {
      return null;
    }
  }
  function ssSet(key, v) {
    try {
      window.sessionStorage.setItem(key, v);
    } catch (e) {}
  }

  function setCookie(name, value, days) {
    var d = new Date();
    d.setTime(d.getTime() + days * 864e5);
    var str =
      name +
      "=" +
      encodeURIComponent(value) +
      ";expires=" +
      d.toUTCString() +
      ";path=/;SameSite=Lax";
    if (C.cookieDomain) str += ";domain=" + C.cookieDomain;
    if (location.protocol === "https:") str += ";Secure";
    document.cookie = str;
  }

  function cookies() {
    var values = Object.create(null);
    document.cookie.split(";").forEach(function (part) {
      var separator = part.indexOf("=");
      if (separator < 0) return;
      var name = part.slice(0, separator).trim();
      if (Object.prototype.hasOwnProperty.call(values, name)) return;
      try {
        values[name] = decodeURIComponent(part.slice(separator + 1).trim());
      } catch (e) {
        values[name] = "";
      }
    });
    return values;
  }

  function getCookie(name) {
    return cookies()[name] || "";
  }

  function qs(name) {
    return new URLSearchParams(location.search).get(name) || "";
  }

  function isClickIdKey(key) {
    return key.replace(/[_-]/g, "").toLowerCase() === "clickid" ||
      ["pfm_cid", "_pfm_cid", "cid"].indexOf(key) > -1 ||
      C.clickParams.indexOf(key) > -1;
  }

  function readClickId(values) {
    if (Object.prototype.hasOwnProperty.call(values, "clickId"))
      return typeof values.clickId === "string" ? values.clickId : "";
    var keys = Object.keys(values);
    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      if (isClickIdKey(key) && typeof values[key] === "string" && values[key])
        return values[key];
    }
    return "";
  }

  function normalizeClickId(values) {
    var clickId = readClickId(values);
    Object.keys(values).forEach(function (key) {
      if (key !== "clickId" && isClickIdKey(key)) delete values[key];
    });
    if (clickId) values.clickId = clickId;
    return values;
  }

  function urlClickId() {
    var values = Object.create(null);
    new URLSearchParams(location.search).forEach(function (value, key) {
      if (!Object.prototype.hasOwnProperty.call(values, key)) values[key] = value;
    });
    return readClickId(values);
  }

  function firstParam(names) {
    for (var i = 0; i < names.length; i++) {
      var v = qs(names[i]);
      if (v) return v;
    }
    return "";
  }

  function money(cents) {
    if (cents === null || cents === undefined || isNaN(cents)) return null;
    return Math.round(Number(cents)) / 100;
  }

  /* ------------------------------------------------------------- identity */

  function sessionId() {
    var s = ssGet(SESSION_KEY);
    if (!s) {
      s = uuid();
      ssSet(SESSION_KEY, s);
    }
    return s;
  }

  function loadIdentity() {
    var id = lsGet(LS_ID, {}) || {};

    // anonymous id: stable per browser
    if (!id.anonymous_id) id.anonymous_id = getCookie("_pfm_aid") || uuid();

    // click id and friends — a fresh value in the URL always wins
    var cid = urlClickId();
    var savedCookies = cookies();
    if (cid) {
      id.clickId = cid;
      id.click_at = now();
    } else {
      id.clickId = id.clickId || savedCookies.clickId ||
        readClickId(id) || readClickId(savedCookies) || "";
    }
    normalizeClickId(id);

    var eid = firstParam(C.entrantParams);
    if (eid) id.entrant_id = eid;
    else if (!id.entrant_id) id.entrant_id = getCookie("_pfm_eid") || "";

    var ref = firstParam(C.refParams);
    if (ref) id.ref_code = ref;

    var aff = firstParam(C.affParams);
    if (aff) id.aff = aff;

    var mbh = firstParam(C.mbhParams);
    if (mbh) id.mbh = mbh;

    // Ad-level context passed through by the tracking link (ids only, never names).
    (C.extraParams || []).forEach(function (p) {
      var v = qs(p);
      if (v) {
        id.extra = id.extra || {};
        id.extra[p] = v.slice(0, 120);
      }
    });

    [
      "fbclid",
      "gclid",
      "gbraid",
      "wbraid",
      "ttclid",
      "msclkid",
      "li_fat_id",
      "twclid",
    ].forEach(function (p) {
      var v = qs(p);
      if (v) id[p] = v;
    });

    [
      "utm_source",
      "utm_medium",
      "utm_campaign",
      "utm_content",
      "utm_term",
    ].forEach(function (p) {
      var v = qs(p);
      if (v) {
        id.utm = id.utm || {};
        id.utm[p] = v;
      }
    });

    // Meta browser id set by the Meta pixel, useful for match quality downstream
    var fbp = getCookie("_fbp");
    if (fbp) id._fbp = fbp;

    if (!id.first_seen) id.first_seen = now();
    id.last_seen = now();

    persistIdentity(id);
    return id;
  }

  function persistIdentity(id) {
    normalizeClickId(id);
    lsSet(LS_ID, id);
    setCookie("_pfm_aid", id.anonymous_id, 400);
    if (id.clickId) setCookie("clickId", id.clickId, C.cookieDays);
    if (id.entrant_id) setCookie("_pfm_eid", id.entrant_id, C.cookieDays);
    if (id.aff) setCookie("_pfm_aff", id.aff, C.cookieDays);
    if (id.mbh) setCookie("_pfm_mbh", id.mbh, C.cookieDays);
    // Not campaign-scoped like the above — persists as long as anonymous_id does.
    if (id.shopify_customer_id) setCookie("_pfm_cust", id.shopify_customer_id, 400);
  }

  var IDENTITY = loadIdentity();

  /** Called by the portal (or any page) once the entrant is known. */
  function identify(entrantId, extra) {
    if (entrantId) IDENTITY.entrant_id = String(entrantId);
    if (extra && typeof extra === "object") {
      var clickId = readClickId(extra);
      if (clickId) IDENTITY.clickId = clickId;
      for (var key in extra)
        if (Object.prototype.hasOwnProperty.call(extra, key) &&
            !isClickIdKey(key) && key !== "__proto__" &&
            key !== "constructor" && key !== "prototype")
          IDENTITY[key] = extra[key];
    }
    persistIdentity(IDENTITY);
    syncCartAttributes(true);
    return IDENTITY;
  }

  var CID_GEN_KEY = "pfm_cid_gen";
  var clickIdPending = null;

  /**
   * Organic visitors (no aff, no clickId) get a clickId minted by PFM so their
   * funnel events can still be attributed. Stored exactly like a URL clickId.
   */
  function ensureClickId() {
    if (IDENTITY.clickId) return Promise.resolve(IDENTITY.clickId);
    if (IDENTITY.aff || getCookie("_pfm_aff")) return Promise.resolve("");
    if (!C.clickIdEndpoint || !originalFetch) return Promise.resolve("");
    if (clickIdPending) return clickIdPending;
    // Another tab/page this session already asked; don't mint a second id.
    if (ssGet(CID_GEN_KEY)) {
      var saved = getCookie("clickId");
      if (saved) {
        IDENTITY.clickId = saved;
        persistIdentity(IDENTITY);
      }
      return Promise.resolve(saved);
    }
    ssSet(CID_GEN_KEY, "1");

    clickIdPending = originalFetch(C.clickIdEndpoint, {
      credentials: "omit",
      headers: { Accept: "application/json" },
    })
      .then(function (r) {
        if (!r.ok) throw new Error("clickId HTTP " + r.status);
        return r.json();
      })
      .then(function (json) {
        var cid = json && typeof json === "object" ? readClickId(json) : "";
        if (!cid) throw new Error("clickId missing in response");
        // A URL/identify() id that arrived meanwhile wins.
        if (!IDENTITY.clickId) {
          IDENTITY.clickId = String(cid);
          IDENTITY.click_at = now();
          IDENTITY.click_generated = true;
          persistIdentity(IDENTITY);
          syncCartAttributes(true);
          log("clickId generated", IDENTITY.clickId);
        }
        return IDENTITY.clickId;
      })
      .catch(function (e) {
        log("clickId generation failed", e);
        try {
          window.sessionStorage.removeItem(CID_GEN_KEY);
        } catch (err) {}
        clickIdPending = null;
        return "";
      });
    return clickIdPending;
  }

  /* ------------------------------------------------- cart attribute mirror */

  var cartSyncDone = false;
  var cartSyncPending = null;

  /** True when we have any attribution signal worth writing onto the cart/order. */
  function hasAttribution() {
    if (
      IDENTITY.clickId ||
      IDENTITY.entrant_id ||
      IDENTITY.ref_code ||
      IDENTITY.aff ||
      IDENTITY.mbh
    )
      return true;
    if (
      IDENTITY.fbclid ||
      IDENTITY.gclid ||
      IDENTITY.gbraid ||
      IDENTITY.wbraid ||
      IDENTITY.ttclid ||
      IDENTITY.msclkid ||
      IDENTITY.li_fat_id ||
      IDENTITY.twclid
    )
      return true;
    if (IDENTITY.utm) {
      for (var k in IDENTITY.utm) if (IDENTITY.utm[k]) return true;
    }
    return false;
  }

  function currentAttributePayload() {
    var attrs = {
      clickId: IDENTITY.clickId || "",
      _pfm_eid: IDENTITY.entrant_id || "",
      _pfm_aid: IDENTITY.anonymous_id || "",
      _pfm_ref: IDENTITY.ref_code || "",
      _pfm_aff: IDENTITY.aff || "",
      _pfm_mbh: IDENTITY.mbh || "",
      _pfm_ad: (IDENTITY.extra && IDENTITY.extra.ad_id) || "",
      _pfm_fbclid: IDENTITY.fbclid || "",
      _pfm_gclid: IDENTITY.gclid || IDENTITY.gbraid || IDENTITY.wbraid || "",
      _pfm_src: (IDENTITY.utm && IDENTITY.utm.utm_source) || "",
      _pfm_camp: (IDENTITY.utm && IDENTITY.utm.utm_campaign) || "",
      _pfm_site: C.siteId || "",
      _pfm_gw: C.campaignId || "",
    };
    // Shopify keeps empty-string attributes out of the order, which is what we want.
    return attrs;
  }

  /**
   * Writes identity onto the cart so it lands in order.note_attributes.
   * Shopify Flow reads these to send Sold to PFM.
   */
  function syncCartAttributes(force) {
    if (!C.cartAttributes || !originalFetch) return Promise.resolve();
    if (cartSyncDone && !force) return Promise.resolve();
    if (!hasAttribution()) return Promise.resolve();

    if (cartSyncPending) {
      return cartSyncPending.then(function () { return syncCartAttributes(force); });
    }
    var body = { attributes: currentAttributePayload() };

    cartSyncPending = originalFetch
      .call(window, cartUrl("update.js"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(body),
        credentials: "same-origin",
        keepalive: true, // survive navigation started right after a checkout click
      })
      .then(function (r) {
        if (!r.ok) throw new Error("Cart sync HTTP " + r.status);
        cartSyncDone = true;
        log("cart attributes synced", r.status);
      })
      .catch(function (e) {
        cartSyncDone = false;
        log("cart attribute sync failed", e);
      })
      .then(function () { cartSyncPending = null; });
    return cartSyncPending;
  }

  function cartUrl(path) {
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
    return root + (path ? "cart/" + path : "cart.js");
  }

  /* -------------------------------------------------------------- consent */

  function consentGranted() {
    if (!C.requireConsent) return true;
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (!cp) return false;
      if (typeof cp.analyticsProcessingAllowed === "function")
        return !!cp.analyticsProcessingAllowed();
      return true;
    } catch (e) {
      return false;
    }
  }

  /* -------------------------------------------------------- transport (1) */

  var originalFetch = window.fetch ? window.fetch.bind(window) : null;

  /**
   * ADAPTER — the only place the PFM wire format lives.
   * PFM's /pb endpoint is a GET postback pixel: clickId + type, nothing else.
   * No clickId means nothing to attribute, so we return no url and skip the send.
   */
  // Client event name -> PFM postback type, where they differ.
  var POSTBACK_TYPES = { CompleteRegistration: "AccountCreation" };

  function buildRequest(payload) {
    normalizeClickId(payload);
    if (!payload.clickId) return { url: "" };
    var qs =
      "clickId=" +
      encodeURIComponent(payload.clickId) +
      "&type=" +
      encodeURIComponent(POSTBACK_TYPES[payload.event] || payload.event);
    return {
      url: C.endpoint + (C.endpoint.indexOf("?") > -1 ? "&" : "?") + qs,
    };
  }

  function send(payload) {
    // Events fired while a clickId is being generated wait for it instead of dropping.
    if (clickIdPending && !readClickId(payload)) {
      return clickIdPending.then(function () {
        payload.clickId = IDENTITY.clickId || null;
        return sendNow(payload);
      });
    }
    return sendNow(payload);
  }

  function sendNow(payload) {
    var req = buildRequest(payload);
    if (!req.url) {
      log("no click id, dropping", payload.event);
      return Promise.resolve(true);
    }

    // Image pixel: GET, no CORS preflight, and the browser keeps it in
    // flight across the navigation a checkout/InitiateCheckout click starts.
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.onload = function () {
        log("pixel sent", payload.event);
        resolve(true);
      };
      img.onerror = function () {
        reject(new Error("pixel failed: " + payload.event));
      };
      img.src = req.url;
    });
  }

  /* ------------------------------------------------------------ retry queue */

  function enqueue(payload) {
    var q = lsGet(LS_QUEUE, []);
    q.push({ payload: payload, tries: 0, at: Date.now() });
    if (q.length > C.maxQueue) q = q.slice(q.length - C.maxQueue);
    lsSet(LS_QUEUE, q);
  }

  var flushing = false;

  function flush() {
    if (flushing) return;
    var q = lsGet(LS_QUEUE, []);
    if (!q.length) return;
    flushing = true;

    q.forEach(function (entry) { normalizeClickId(entry.payload); });
    lsSet(LS_QUEUE, q);
    var item = q[0];
    send(item.payload)
      .then(function () {
        var cur = lsGet(LS_QUEUE, []);
        cur.shift();
        lsSet(LS_QUEUE, cur);
        flushing = false;
        if (cur.length) setTimeout(flush, 250);
      })
      .catch(function () {
        var cur = lsGet(LS_QUEUE, []);
        if (cur.length) {
          cur[0].tries = (cur[0].tries || 0) + 1;
          if (cur[0].tries >= 5) cur.shift(); // give up, don't block the queue
          lsSet(LS_QUEUE, cur);
        }
        flushing = false;
      });
  }

  /* --------------------------------------------------------------- dedupe */

  function isDuplicate(key) {
    var map = lsGet(LS_DEDUPE, {}) || {};
    var t = Date.now();
    for (var k in map) if (t - map[k] > C.dedupeWindowMs) delete map[k];
    if (map[key] && t - map[key] < C.dedupeWindowMs) {
      lsSet(LS_DEDUPE, map);
      return true;
    }
    map[key] = t;
    lsSet(LS_DEDUPE, map);
    return false;
  }

  /* ---------------------------------------------------------- event model */

  function shopContext() {
    var s = window.Shopify || {};
    var meta = (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) || {};
    return {
      shop_domain: s.shop || location.hostname,
      currency: (s.currency && s.currency.active) || meta.currency || "USD",
      page_type:
        (meta.page && meta.page.pageType) || (s.designMode ? "design" : ""),
      customer_id: (meta.page && meta.page.customerId) || null,
      theme_id: s.theme && s.theme.id ? String(s.theme.id) : null,
    };
  }

  function basePayload(eventName, data) {
    var ctx = shopContext();
    return {
      event: eventName, // AddToCart | InitiateCheckout | ViewContent | SubmitForm | CompleteRegistration
      event_id: uuid(), // idempotency key for PFM
      event_time: now(),
      source: "shopify_theme",
      site_id: C.siteId || null,
      campaign_id: C.campaignId || null,
      shop_domain: ctx.shop_domain,
      page_type: ctx.page_type || null,
      page_url: location.href,
      referrer: document.referrer || null,
      session_id: sessionId(),
      anonymous_id: IDENTITY.anonymous_id || null,
      clickId: IDENTITY.clickId || null,
      entrant_id: IDENTITY.entrant_id || null,
      ref_code: IDENTITY.ref_code || null,
      aff: IDENTITY.aff || null,
      ad_context: IDENTITY.extra || null,
      customer_id: ctx.customer_id ? String(ctx.customer_id) : null,
      fbclid: IDENTITY.fbclid || null,
      fbp: IDENTITY._fbp || null,
      gclid: IDENTITY.gclid || IDENTITY.gbraid || IDENTITY.wbraid || null,
      ttclid: IDENTITY.ttclid || null,
      utm: IDENTITY.utm || null,
      currency: (data && data.currency) || ctx.currency,
      value: data && data.value !== undefined ? data.value : null,
      products: (data && data.products) || null,
      order_id: (data && data.order_id) || null,
      user_agent: navigator.userAgent,
      properties: (data && data.properties) || null,
    };
  }

  function track(eventName, data, opts) {
    opts = opts || {};
    if (!consentGranted()) {
      log("consent missing, skipped", eventName);
      return null;
    }

    var dedupeKey =
      eventName +
      "|" +
      (opts.dedupeKey || JSON.stringify((data && data.products) || data || {}));
    if (!opts.skipDedupe && isDuplicate(dedupeKey)) {
      log("duplicate skipped", eventName);
      return null;
    }

    var payload = basePayload(eventName, data);
    log("track", eventName, payload);

    send(payload).catch(function () {
      enqueue(payload);
    });
    return payload;
  }

  /* ---------------------------------------------------------- ViewContent */

  function productFromMeta() {
    var meta = (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) || {};
    var p = meta.product;
    if (!p) return null;
    var variant = (p.variants && p.variants[0]) || {};
    return {
      products: [
        {
          product_id: String(p.id),
          variant_id: variant.id ? String(variant.id) : null,
          sku: variant.sku || null,
          title:
            p.variants && p.variants[0] ? p.variants[0].name : p.type || null,
          quantity: 1,
          price: money(variant.price),
        },
      ],
      value: money(variant.price),
    };
  }

  function maybeViewContent() {
    if (!C.trackViewContent) return;
    var ctx = shopContext();
    var pages = C.viewContentPages || [];
    var wanted = pages.indexOf("all") > -1 || pages.indexOf(ctx.page_type) > -1;
    if (!wanted) return;

    var data = (ctx.page_type === "product" && productFromMeta()) || {
      properties: { page_type: ctx.page_type },
    };
    track("ViewContent", data, { dedupeKey: location.pathname });
  }

  /** Wait for real engagement (scroll or click) before firing ViewContent — skips pure bounces. */
  function armViewContent() {
    if (!C.trackViewContent) return;
    var fired = false;
    function trigger() {
      if (fired) return;
      fired = true;
      document.removeEventListener("scroll", trigger, true);
      document.removeEventListener("click", trigger, true);
      maybeViewContent();
    }
    document.addEventListener("scroll", trigger, true);
    document.addEventListener("click", trigger, true);
  }

  /* ------------------------------------------------------------ AddToCart */

  function normalizeAddToCartResponse(json) {
    if (!json) return null;
    var items = json.items || (json.id ? [json] : null);
    if (!items || !items.length) return null;
    var products = items.map(function (it) {
      return {
        product_id: it.product_id ? String(it.product_id) : null,
        variant_id: it.variant_id
          ? String(it.variant_id)
          : it.id
            ? String(it.id)
            : null,
        sku: it.sku || null,
        title: it.product_title || it.title || null,
        quantity: it.quantity || 1,
        price: money(it.price),
      };
    });
    var value = products.reduce(function (sum, p) {
      return sum + (p.price || 0) * (p.quantity || 1);
    }, 0);
    return { products: products, value: Math.round(value * 100) / 100 };
  }

  function handleAddToCart(json) {
    syncCartAttributes(true);
    if (!C.trackAddToCart) return;
    var data = normalizeAddToCartResponse(json);
    if (!data) return;
    track("AddToCart", data, {
      dedupeKey: data.products
        .map(function (p) {
          return p.variant_id + "x" + p.quantity;
        })
        .join(","),
    });
  }

  function isCartAdd(url) {
    return typeof url === "string" && url.indexOf("/cart/add") > -1;
  }
  function isCartMutation(url) {
    return (
      typeof url === "string" &&
      (url.indexOf("/cart/change") > -1 || url.indexOf("/cart/update") > -1)
    );
  }

  // fetch interception
  if (window.fetch) {
    window.fetch = function (input, init) {
      var url = typeof input === "string" ? input : (input && input.url) || "";
      var p = originalFetch(input, init);
      if (isCartAdd(url)) {
        p.then(function (res) {
          if (!res || !res.ok) return;
          res
            .clone()
            .json()
            .then(handleAddToCart)
            .catch(function () {});
        }).catch(function () {});
      } else if (isCartMutation(url)) {
        p.then(function () {
          cartSyncDone = false;
        }).catch(function () {});
      }
      return p;
    };
  }

  // XHR interception (older themes, some cart drawers)
  (function () {
    var open = XMLHttpRequest.prototype.open;
    var srequest = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (method, url) {
      this.__pfmUrl = url;
      return open.apply(this, arguments);
    };
    XMLHttpRequest.prototype.send = function () {
      var xhr = this;
      if (isCartAdd(xhr.__pfmUrl)) {
        xhr.addEventListener("load", function () {
          if (xhr.status < 200 || xhr.status >= 300) return;
          handleAddToCart(safeParse(xhr.responseText, null));
        });
      }
      return srequest.apply(this, arguments);
    };
  })();

  // Non-AJAX add-to-cart form posts (page reloads before a fetch would resolve).
  // Deferred to a macrotask so evt.defaultPrevented reflects whatever the theme's
  // own submit handler decided — themes that preventDefault() and fetch /cart/add
  // themselves (e.g. product-form.js) are already covered by the fetch/XHR
  // interception above, and must not also fire here or AddToCart double-counts.
  document.addEventListener(
    "submit",
    function (e) {
      var form = e.target;
      if (!form || !form.action || form.action.indexOf("/cart/add") === -1)
        return;
      if (!C.trackAddToCart) return;
      setTimeout(function () {
        if (e.defaultPrevented) return;
        var qty = 1,
          variant = null;
        try {
          var fd = new FormData(form);
          qty = Number(fd.get("quantity")) || 1;
          variant = fd.get("id") ? String(fd.get("id")) : null;
        } catch (err) {}
        track(
          "AddToCart",
          {
            products: [
              {
                product_id: null,
                variant_id: variant,
                quantity: qty,
                price: null,
              },
            ],
          },
          { dedupeKey: "form:" + variant + "x" + qty },
        );
      }, 0);
    },
    true,
  );

  /* ----------------------------------------------------- InitiateCheckout */

  function cartSnapshot() {
    if (!originalFetch) return Promise.reject(new Error("Fetch unavailable"));
    return originalFetch(cartUrl(""), {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        return {
          value: money(cart.total_price),
          currency: cart.currency,
          products: (cart.items || []).map(function (it) {
            return {
              product_id: String(it.product_id),
              variant_id: String(it.variant_id),
              sku: it.sku || null,
              title: it.product_title || it.title,
              quantity: it.quantity,
              price: money(it.price),
            };
          }),
          properties: { item_count: cart.item_count, cart_token: cart.token },
        };
      });
  }

  var checkoutFired = false;

  function handleInitiateCheckout() {
    var sync = syncCartAttributes(true);
    if (!C.trackInitiateCheckout || checkoutFired) return sync;
    checkoutFired = true;
    setTimeout(function () {
      checkoutFired = false;
    }, 3000);

    // Fire immediately with what we know; enrich from /cart.js if it resolves in time.
    var fired = false;
    var timer = setTimeout(function () {
      if (fired) return;
      fired = true;
      track(
        "InitiateCheckout",
        {},
        { dedupeKey: "checkout:" + sessionId() },
      );
    }, 350);

    cartSnapshot()
      .then(function (data) {
        if (fired) return;
        fired = true;
        clearTimeout(timer);
        track("InitiateCheckout", data, {
          dedupeKey:
            "checkout:" + (data.properties && data.properties.cart_token),
        });
      })
      .catch(function () {});

    return sync;
  }

  document.addEventListener(
    "click",
    function (e) {
      var el =
        e.target && e.target.closest
          ? e.target.closest('button, a, input[type="submit"]')
          : null;
      if (!el) return;
      var name = (el.getAttribute && el.getAttribute("name")) || "";
      var href = (el.getAttribute && el.getAttribute("href")) || "";
      var inDynamic =
        el.closest &&
        el.closest(
          '.shopify-payment-button, shopify-accelerated-checkout, [data-shopify="payment-button"]',
        );
      var checkoutLink = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?checkout(?:[/?#]|$)/i.test(href);
      if (name === "checkout" || checkoutLink || inDynamic) {
        var sync = handleInitiateCheckout();
        if (checkoutLink && !e.defaultPrevented && e.button === 0 &&
            !e.metaKey && !e.ctrlKey && !e.shiftKey && !e.altKey &&
            (!el.target || el.target === "_self") && C.cartAttributes && hasAttribution()) {
          e.preventDefault();
          Promise.race([sync, new Promise(function (resolve) { setTimeout(resolve, 1500); })])
            .then(function () { window.location.assign(href); });
        }
      }
    },
    true,
  );

  document.addEventListener(
    "submit",
    function (e) {
      var form = e.target;
      if (!form || !form.action) return;
      if (
        form.action.indexOf("/checkout") > -1 ||
        (/\/cart\/?(?:[?#]|$)/.test(form.action) &&
          form.querySelector('[name="checkout"]'))
      ) {
        if (C.cartAttributes && hasAttribution()) {
          var attrs = currentAttributePayload();
          Object.keys(attrs).forEach(function (key) {
            var name = "attributes[" + key + "]";
            var input = form.querySelector('[name="' + name + '"]');
            if (!input) {
              input = document.createElement("input");
              input.type = "hidden";
              input.name = name;
              form.appendChild(input);
            }
            input.value = attrs[key];
          });
        }
        handleInitiateCheckout();
      }
    },
    true,
  );

  /* ------------------------------------------------- SubmitForm (optional) */

  if (C.submitFormSelector) {
    document.addEventListener(
      "submit",
      function (e) {
        var form = e.target;
        if (!form || !form.matches || !form.matches(C.submitFormSelector))
          return;
        var props = {};
        try {
          var fd = new FormData(form);
          fd.forEach(function (v, key) {
            if (/pass|card|cvv|token/i.test(key)) return; // never forward secrets
            if (/email|phone|tel/i.test(key)) {
              props[key + "_present"] = !!v;
              return;
            } // PII stays out of the browser payload
            props[key] = String(v).slice(0, 120);
          });
        } catch (err) {}
        track(
          "SubmitForm",
          { properties: props },
          { skipDedupe: true },
        );
      },
      true,
    );
  }

  /* ------------------------------------------------------------ lifecycle */

  // Start before boot and the pfmQueue replay so early events wait for the id.
  ensureClickId();

  function boot() {
    syncCartAttributes();
    armViewContent();
    flush();
  }

  if (document.readyState === "loading")
    document.addEventListener("DOMContentLoaded", boot);
  else boot();

  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState === "visible") flush();
  });
  window.addEventListener("pageshow", function () {
    flush();
  });

  /* -------------------------------------------------------- public surface */

  window.pfm = {
    track: track, // pfm.track('CompleteRegistration', {...})
    identify: identify, // pfm.identify('<entrant id>')
    identity: function () {
      return IDENTITY;
    },
    syncCart: function () {
      return syncCartAttributes(true);
    },
    attributes: currentAttributePayload, // what the order will carry
    ensureClickId: ensureClickId, // await before reading the clickId cookie
    flush: flush,
    config: C,
    version: "1.1.0",
  };

  // Anything queued before the script loaded: window.pfmQueue = [['track','AddToCart',{...}], ...]
  if (window.pfmQueue && window.pfmQueue.length) {
    window.pfmQueue.forEach(function (args) {
      var fn = window.pfm[args[0]];
      if (typeof fn === "function") fn.apply(null, args.slice(1));
    });
    window.pfmQueue = [];
  }

  log("ready", IDENTITY);
})(window, document);
